import type {
  ResolvedWorkflowStep,
  WorkflowContext,
  WorkflowDefinition,
  WorkflowOverlay,
  WorkflowOverlayContext,
  WorkflowStepModule,
} from "../types";

const LINKEDIN_SURFACES = new Set(["linkedin", "linkedin_personal", "linkedin_derivative"]);
const YOUTUBE_SURFACES = new Set(["youtube", "youtube_personal"]);
const BLOG_SURFACES = new Set(["blog", "docs", "documentation"]);
const PLATFORM_SURFACE_FALLBACKS: Record<string, string> = {
  linkedin: "linkedin",
  youtube: "youtube",
  website: "blog",
};

function normalizeSurfaceKey(surface: string | null | undefined): string | null {
  return typeof surface === "string" && surface.trim()
    ? surface.trim().toLowerCase()
    : null;
}

export function surfaceWorkflowOverlaySignature(
  context: WorkflowOverlayContext,
): string | null {
  const surface = normalizeSurfaceKey(context.brief.surface);
  if (surface) return surface;

  const platform = normalizeSurfaceKey(context.brief.platform);
  return platform ? (PLATFORM_SURFACE_FALLBACKS[platform] ?? null) : null;
}

function mergeNotes(...groups: Array<string[] | undefined>): string[] | undefined {
  const values = groups.flatMap((group) => group ?? []).map((value) => value.trim()).filter(Boolean);
  return values.length > 0 ? [...new Set(values)] : undefined;
}

function prioritizeStepOrder(baseSteps: string[], orderOverride: string[] | undefined): string[] {
  if (!orderOverride || orderOverride.length === 0) return [...baseSteps];
  const pinned = orderOverride.filter((stepId) => baseSteps.includes(stepId));
  const remainder = baseSteps.filter((stepId) => !pinned.includes(stepId));
  // Step-order precedence is last-overlay-wins because overlays are reduced in
  // sequence by `mergeWorkflowOverlays()`.
  return [...pinned, ...remainder];
}

export function mergeWorkflowOverlays(
  definition: WorkflowDefinition,
  stepRegistry: (stepId: string) => WorkflowStepModule | null,
  overlays: WorkflowOverlay[],
): { steps: string[]; resolvedSteps: ResolvedWorkflowStep[] } {
  const filteredOverlays = overlays.filter((overlay) => overlay.steps || overlay.stepOrder);
  if (import.meta.env.DEV) {
    for (const overlay of filteredOverlays) {
      for (const stepId of overlay.stepOrder ?? []) {
        if (!definition.steps.includes(stepId)) {
          console.warn(`[workflows] overlay ${overlay.id} ignored unknown stepOrder entry: ${stepId}`);
        }
      }
      for (const stepId of Object.keys(overlay.steps ?? {})) {
        if (!definition.steps.includes(stepId)) {
          console.warn(`[workflows] overlay ${overlay.id} ignored unknown step config: ${stepId}`);
        }
      }
    }
  }
  const orderedStepIds = filteredOverlays.reduce(
    (current, overlay) => prioritizeStepOrder(current, overlay.stepOrder),
    [...definition.steps],
  );
  const uniqueOrderedStepIds = [...new Set(orderedStepIds)];

  const resolvedSteps = uniqueOrderedStepIds.flatMap((stepId) => {
    const step = stepRegistry(stepId);
    if (!step) throw new Error(`Unknown workflow step: ${stepId}`);

    const overlayConfigs = filteredOverlays
      .map((overlay) => overlay.steps?.[stepId])
      .filter((config): config is NonNullable<typeof config> => Boolean(config));

    // `enabled: false` is a hard veto. Any overlay can remove a step entirely,
    // even though param override precedence remains last-wins.
    if (overlayConfigs.some((config) => config.enabled === false)) {
      return [];
    }

    const notes = mergeNotes(...overlayConfigs.map((config) => config.notes));
    // Overlay precedence is last-wins for duplicated param keys. That lets
    // surface defaults be refined by later brand-specific overrides.
    const paramOverrides = overlayConfigs.reduce<Record<string, unknown>>((acc, config) => ({
      ...acc,
      ...(config.paramOverrides ?? {}),
    }), {});
    const hasParamOverrides = Object.keys(paramOverrides).length > 0;
    if (hasParamOverrides && !step.buildParams && import.meta.env.DEV) {
      console.warn(`[workflows] overlay params for step ${step.id} are ignored because the step has no buildParams.`);
    }
    const buildParams = step.buildParams
      ? (context: WorkflowContext) => ({
          ...step.buildParams!(context),
          ...paramOverrides,
        })
      : undefined;

    return [{
      ...step,
      notes,
      paramOverrides: hasParamOverrides ? paramOverrides : undefined,
      buildParams,
    } satisfies ResolvedWorkflowStep];
  });

  return {
    steps: resolvedSteps.map((step) => step.id),
    resolvedSteps,
  };
}

export function surfaceWorkflowOverlay(
  context: WorkflowOverlayContext,
): WorkflowOverlay | null {
  const surface = surfaceWorkflowOverlaySignature(context);
  if (!surface) return null;

  if (LINKEDIN_SURFACES.has(surface)) {
    return {
      id: "surface:linkedin",
      steps: {
        content_validation: {
          notes: [
            "LinkedIn drafts should prove one operator insight before they broaden into positioning.",
          ],
        },
        language_clarity_review: {
          paramOverrides: {
            surface: "linkedin",
            strictness: "medium",
          },
          notes: [
            "Prefer lesson-learned language over category jargon on LinkedIn.",
          ],
        },
      },
    };
  }

  if (BLOG_SURFACES.has(surface)) {
    return {
      id: "surface:blog",
      stepOrder: ["content_validation", "voice_review", "language_clarity_review", "approval_gate"],
      steps: {
        language_clarity_review: {
          paramOverrides: {
            surface: "blog",
            strictness: "high",
          },
          notes: [
            "Blog copy can be technical, but claims still need concrete observable wording.",
          ],
        },
      },
    };
  }

  if (YOUTUBE_SURFACES.has(surface)) {
    return {
      id: "surface:youtube",
      stepOrder: ["content_validation", "voice_review", "approval_gate"],
      steps: {
        language_clarity_review: {
          enabled: false,
        },
        content_validation: {
          notes: [
            "For YouTube, validate the promise and proof angle first; spoken-script clarity review comes later in the video workflow.",
          ],
        },
      },
    };
  }

  return null;
}
